import {
  Droplets, Car, Zap, Mountain, Factory, Leaf, Wheat,
} from "lucide-react";

/**
 * Per-industry deep content for the expandable Industries section on /nexus.
 * Each industry exposes: typical assets, sensor stack, rule patterns, analytics outputs.
 * Keep copy operator-led, third-person, industrial.
 */
export const NEXUS_INDUSTRY_DETAIL = [
  {
    slug: "water",
    name: "Water & wastewater",
    icon: Droplets,
    tagline: "Reservoirs, abstraction, dosing, and distribution — online and automated.",
    intro:
      "Municipal utilities, farming operations, bottling plants, and dosing facilities run Nexus as the live brain across the pipework. Tank levels, pump health, leak signatures, and chlorine dosing all flow into the same canvas — with rules that act before the operator's phone rings.",
    typicalAssets: [
      "Reservoirs, header tanks, sumps, balance tanks",
      "Booster pumps, transfer pumps, dosing pumps",
      "Pressure-reducing valves, motorised actuators",
      "Flow meters at abstraction, line, and outlet points",
      "Chlorine, pH, turbidity, conductivity probes",
    ],
    sensors: [
      "Level (radar, hydrostatic, ultrasonic)",
      "Pressure (4–20 mA transmitters, line and discharge)",
      "Flow (mag, mechanical, pulse counting)",
      "Water quality (pH, EC, ORP, turbidity, residual chlorine)",
      "Motor current, vibration, run-hours",
    ],
    rules: [
      "Tank-low → start backup pump, alert supervisor, log to compliance",
      "Discharge pressure climbing while flow steady → flag blockage, schedule strainer clean",
      "Chlorine dose drifts > 0.5 ppm from setpoint → pause line, escalate",
      "Bore aquifer drawdown > daily limit → enforce abstraction quota",
    ],
    analytics: [
      "Daily volumes by source, by line, by customer",
      "Energy per kilolitre — pump efficiency drift over time",
      "Non-revenue water signature analysis from balance vs delivery",
      "Compliance reports — chlorine and pH residuals, sampled & exported",
    ],
  },
  {
    slug: "agriculture",
    name: "Agriculture & irrigation",
    icon: Wheat,
    tagline: "Zones, soil moisture, fertigation, and pump health — one canvas per farm.",
    intro:
      "Commercial farms run the entire irrigation operation from Nexus — every zone, every dosing line, every pump, every soil probe. Schedules adapt to soil moisture in real time. Dosing calculations run on the gateway and fall back to safe baselines if the cloud blinks. The farmer wakes up to a one-page brief, not a thousand alarms.",
    typicalAssets: [
      "Zone solenoids, manifold control valves, fertigation pumps",
      "Pivot endguns, microjet & drip blocks, frost-protection valves",
      "Booster pumps, balance tanks, header tanks, sumps",
      "Soil moisture stations and weather masts",
    ],
    sensors: [
      "Soil moisture, temperature, salinity at multiple depths",
      "Weather station — rainfall, ET, wind, leaf wetness, solar radiation",
      "Pressure on each manifold, flow on each line",
      "EC & pH at the dosing skid, tank levels, dosing-pump strokes",
      "Pump current, vibration, run-hours",
    ],
    rules: [
      "Irrigate zone-N when soil moisture < threshold AND ET deficit > target",
      "Stop irrigation if mainline pressure drops > 15% — likely line break",
      "Fertigation: dose to EC setpoint, abort if pH falls outside band",
      "Frost-protection: start sprinklers when ambient < 1.5 °C AND falling",
    ],
    analytics: [
      "Water and fertiliser cost per hectare, per crop, per cycle",
      "Pump efficiency baseline vs current — wear detection",
      "Irrigation uniformity (Christiansen / DU) per block from line flows",
      "Auto-generated grower reports for compliance and audit",
    ],
  },
  {
    slug: "automotive",
    name: "Automotive & vehicle tracking",
    icon: Car,
    tagline: "Fleet position, condition, and behaviour — live across the operation.",
    intro:
      "Logistics, mining haul fleets, agricultural contractors, and service organisations track every vehicle as a first-class asset in Nexus. GNSS position, CAN bus telemetry, driver behaviour, and on-vehicle alerts join the same rule engine that runs the rest of the operation.",
    typicalAssets: [
      "Light vehicles, heavy haulers, mining trucks, generators on trailers",
      "Trailers and yellow plant with onboard nodes",
      "Driver-card readers, dash cameras, panic buttons",
      "Fuel bowsers and depot pumps",
    ],
    sensors: [
      "GNSS position, speed, heading, altitude",
      "CAN-bus telemetry — RPM, fuel rate, coolant temp, DPF, fault codes",
      "Driver behaviour — harsh braking, cornering, acceleration",
      "Fuel level, tilt, door open/close, ignition state, temperature trailers",
    ],
    rules: [
      "Vehicle outside geofence + ignition on → notify dispatch, lock starter",
      "Harsh-event score > threshold over rolling 7 days → flag for coaching",
      "Coolant temp climbing for 10 min → schedule depot inspection",
      "Trailer door opened off-route → alert security with last 3 stops",
    ],
    analytics: [
      "Utilisation, idle ratio, and L/100 km per vehicle",
      "Driver scorecards with trend, ranked across the fleet",
      "Depot fuel reconciliation vs onboard consumption",
      "Geofence dwell, route adherence, and shift compliance reports",
    ],
  },
  {
    slug: "power",
    name: "Power & utilities",
    icon: Zap,
    tagline: "Generation, distribution, and demand — synchronised down to the second.",
    intro:
      "Solar farms, microgrids, substations, and demand-response loads converge on Nexus as a real-time control surface. Inverter strings, breakers, and load groups are addressable channels. Rules execute setpoint changes and breaker actions inside the gateway — no round-trip to the cloud.",
    typicalAssets: [
      "PV strings, inverters, combiner boxes, trackers",
      "Battery storage, BMS, AC-side breakers",
      "Substation switchgear, transformers, capacitor banks",
      "Demand-response loads — pumps, chillers, electrolysers",
    ],
    sensors: [
      "Power-quality meters — V, I, P, Q, THD, frequency",
      "Inverter telemetry, string-level current, panel-level voltages",
      "Battery SoC, SoH, cell-level voltages, thermal probes",
      "Switchgear position, breaker trip flags, alarm contacts",
    ],
    rules: [
      "Inverter string output < 80% of peers → flag for soiling / fault",
      "Battery SoC drops below reserve → curtail non-critical loads",
      "Tariff peak window approaching → shed loads in priority order",
      "Substation alarm contact + breaker trip → escalate with map",
    ],
    analytics: [
      "Performance ratio, specific yield, and availability per asset",
      "Tariff-aware demand profile and cost per kWh consumed",
      "Predicted yield vs actual — clear-sky model deviation",
      "Switchgear ageing — operations counter, trip energy, fault history",
    ],
  },
  {
    slug: "mining",
    name: "Mining & minerals",
    icon: Mountain,
    tagline: "Tailings, conveyors, environmental compliance, and yellow-plant vitals.",
    intro:
      "Open-cast and underground operations bring Nexus to the surface, the workshop, and the tailings facility. Conveyor health, dewatering, ventilation, and environmental compliance all share the same hierarchy — and the same audit trail.",
    typicalAssets: [
      "Conveyor systems, screens, crushers, mill feeders",
      "Tailings dams, decant towers, return-water sumps",
      "Dewatering pumps, ventilation fans, refuge chambers",
      "Workshop equipment, lubrication skids, compressors",
    ],
    sensors: [
      "Belt speed, drift, tonnage (load cells), pull-cord & misalignment",
      "Tailings water levels, freeboard, beach distance, seepage flow",
      "Vibration on bearings & gearboxes (predictive)",
      "Air quality — CO, NOx, methane, dust at portal & in haul roads",
    ],
    rules: [
      "Tailings freeboard below safety margin → lockout deposition",
      "Conveyor pull-cord fired → stop drive, alarm with last 30 s of telemetry",
      "Bearing vibration crosses ISO 10816 zone-C → schedule grease + flag for inspection",
      "Methane > setpoint in section → ventilation boost, evacuation alert",
    ],
    analytics: [
      "Conveyor availability, MTBF, and tonnage moved per shift",
      "Tailings water balance — abstraction vs return, seepage tracking",
      "Environmental compliance pack — daily, signed, exportable",
      "Asset-care library: vibration trends, oil sample history, intervention log",
    ],
  },
  {
    slug: "manufacturing",
    name: "Manufacturing & industrial",
    icon: Factory,
    tagline: "OEE, energy intensity, condition monitoring — on one operator canvas.",
    intro:
      "Discrete and process plants use Nexus alongside their existing PLCs as a real-time supervisory layer. Production counts, energy intensity, downtime reasons, and condition-monitoring signals stream into the same dashboards and feed the same rules — without ripping out controls.",
    typicalAssets: [
      "Production lines, packaging cells, robot cells, AGVs",
      "Boilers, compressors, chillers, refrigeration plant",
      "Spray booths, ovens, dryers, mixers, reactors",
      "Utility metering — electricity, gas, steam, compressed air, water",
    ],
    sensors: [
      "Pulse counters on production output, reject counters",
      "Energy sub-meters per line, per process, per shift",
      "Temperature, pressure, humidity in process zones",
      "Vibration & current on motors, compressors, and chillers",
    ],
    rules: [
      "Reject rate > 1.2× rolling 7-day baseline → flag for QA",
      "Compressor running unloaded > 15 min → notify utilities team",
      "Cold-room temperature out of band → escalate, log for HACCP",
      "OEE for line falls below shift target → real-time supervisor alert",
    ],
    analytics: [
      "OEE per line with availability / performance / quality breakdown",
      "Energy per unit produced — drift, anomalies, ranked by impact",
      "Downtime Pareto by reason code, by shift, by line",
      "Predictive: compressor / chiller efficiency drift, scheduled actions",
    ],
  },
  {
    slug: "environmental",
    name: "Environmental & built environment",
    icon: Leaf,
    tagline: "Air quality, indoor comfort, sub-metering, and compliance for any campus.",
    intro:
      "Commercial real estate, hospitals, universities, and environmental consultancies put Nexus on the roof, in the duct, and across the campus. Air-quality, energy, and occupancy data join compliance reporting — every minute, every site, every tenant.",
    typicalAssets: [
      "Air-quality stations, weather masts, noise loggers",
      "HVAC plant — AHUs, chillers, boilers, VAV boxes",
      "Sub-meters per tenant, per floor, per zone",
      "Water-quality probes for cooling towers and grey-water systems",
    ],
    sensors: [
      "PM2.5, PM10, CO₂, VOC, NO₂, O₃, temperature, humidity",
      "Occupancy via PIR, CO₂, or people-counting cameras",
      "Light, noise, illuminance",
      "Electricity, water, gas sub-meters with pulse capture",
    ],
    rules: [
      "CO₂ in classroom > 1000 ppm → boost AHU fresh-air damper",
      "Noise > by-law limit on construction site → notify foreman",
      "Cooling tower legionella risk window → trigger biocide dose",
      "Energy use after-hours > baseline → alert facilities manager",
    ],
    analytics: [
      "Net-zero scorecard with scope-1 / scope-2 / scope-3 breakdown",
      "Tenant energy and water cost-recovery reports",
      "Indoor-air-quality compliance against ASHRAE / WELL benchmarks",
      "Building tune-up suggestions — Nexi-authored from operational data",
    ],
  },
];

/**
 * Roles & user management — surfaces the multi-user reality of the platform.
 * The portal's "OWNER" badge in the demo confirms a role system; expand the marketing here.
 */
export const NEXUS_ROLES = [
  {
    name: "Owner",
    desc: "Full control of the organisation — billing, branding, sub-org provisioning, role assignment, and audit access. One per tenant, typically the operations director or business owner.",
    rights: ["Billing & subscription", "Org branding & sub-domain", "Role catalogue", "Audit log access", "API key issuance"],
  },
  {
    name: "Administrator",
    desc: "Day-to-day platform management. Adds users, builds sites, provisions gateways and nodes, configures rules, and curates the dashboard library.",
    rights: ["User invitations & role bindings", "Site & gateway lifecycle", "Rule authoring & publication", "Dashboard sharing", "Integration setup"],
  },
  {
    name: "Operator",
    desc: "The control-room user. Reads every channel, acknowledges alarms, executes guarded control commands, and curates personal tab layouts.",
    rights: ["Read-all channels", "Alarm acknowledgement", "Guarded control commands", "Personal tab editing", "Shift-handover reports"],
  },
  {
    name: "Technician",
    desc: "Field-focused. Sees the assets they're rostered to, marks devices as in-maintenance, applies firmware updates, and closes work orders linked to alarms.",
    rights: ["Scoped device view by assignment", "Maintenance mode toggle", "OTA firmware deployment", "Alarm-linked work orders"],
  },
  {
    name: "Analyst",
    desc: "Read-only across the data plane plus Analytics. Composes reports, builds export pipelines, and asks Nexi for cross-site insights.",
    rights: ["Read-only channels & history", "Report builder & scheduler", "Data warehouse export", "Nexi analytical queries"],
  },
  {
    name: "Client / viewer",
    desc: "External stakeholders — landlords, regulators, agronomists, or board members. Sees only the dashboards explicitly shared with them, with read-only access.",
    rights: ["Shared dashboards only", "No control commands", "No data export", "Branded sub-tenant view"],
  },
];

/**
 * Live data callouts shown beneath the product screenshot — marketing-led not literal.
 * Replaces the earlier verbatim portal stats (11 areas, ALL CLEAR, etc.) with operator-led promises.
 */
export const NEXUS_PRODUCT_CALLOUTS = [
  { label: "Channels", value: "Unlimited", sub: "Every sensor, every signal, every site — no licence ceiling." },
  { label: "Canvas", value: "Yours to shape", sub: "Tabs, widgets, density — composed by the operator, not a consultant." },
  { label: "Alerts", value: "Instant action", sub: "Live alert state in the top bar; one click to acknowledge, one rule to resolve." },
  { label: "Navigation", value: "All sites, one menu", sub: "Customisable sidebar across every organisation, site, and gateway." },
];
